(() => {
  let mapsPromise;

  const loadMaps = (apiKey) => {
    if (window.google?.maps?.importLibrary) return Promise.resolve(window.google.maps);
    if (mapsPromise) return mapsPromise;

    mapsPromise = new Promise((resolve, reject) => {
      const callbackName = '__glitzGoogleMapsReady';
      const oldAuthFailure = window.gm_authFailure;

      window.gm_authFailure = () => {
        reject(new Error('Google Maps authentication failed.'));
        if (typeof oldAuthFailure === 'function') oldAuthFailure();
      };

      window[callbackName] = () => {
        resolve(window.google?.maps);
        try { delete window[callbackName]; } catch (_) {}
      };

      const script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&loading=async&libraries=places&v=weekly&callback=' +
        callbackName;
      script.async = true;
      script.defer = true;
      script.dataset.glitzGoogleMaps = 'true';
      script.onerror = () => reject(new Error('Google Maps JavaScript API failed to load.'));
      document.head.appendChild(script);
    });

    return mapsPromise;
  };

  const makeStars = (rating) => {
    const rounded = Math.max(1, Math.min(5, Math.round(Number(rating) || 0)));
    return '★'.repeat(rounded) + '☆'.repeat(5 - rounded);
  };

  const createReviewCard = (review) => {
    const article = document.createElement('article');
    article.className = 'glitz-google-review-card';

    const top = document.createElement('div');
    top.className = 'glitz-google-card-top';

    const attribution = review.authorAttribution;
    const name = attribution?.displayName || 'Google reviewer';

    if (attribution?.uri) {
      const link = document.createElement('a');
      link.href = attribution.uri;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = name;
      top.appendChild(link);
    } else {
      const strong = document.createElement('strong');
      strong.textContent = name;
      top.appendChild(strong);
    }

    const when = document.createElement('span');
    when.textContent =
      review.relativePublishTimeDescription ||
      (review.publishTime instanceof Date ? review.publishTime.toLocaleDateString() : '');
    top.appendChild(when);

    const stars = document.createElement('div');
    stars.className = 'glitz-google-stars';
    stars.textContent = makeStars(review.rating);
    stars.setAttribute('aria-label', String(review.rating || '') + ' out of 5 stars');

    const copy = document.createElement('p');
    copy.textContent = review.text || '';

    article.append(top, stars, copy);
    return article;
  };

  const hydrate = async (section) => {
    if (section.dataset.liveEnabled !== 'true') return;

    const apiKey = (section.dataset.apiKey || '').trim();
    const placeId = (section.dataset.placeId || '').trim();
    if (!apiKey || !placeId) return;

    const ratingNode = section.querySelector('[data-glitz-live-rating]');
    const countNode = section.querySelector('[data-glitz-live-count]');
    const badgeNode = section.querySelector('[data-glitz-live-badge]');
    const summaryNode = section.querySelector('[data-glitz-review-summary]');
    const grid = section.querySelector('[data-glitz-review-grid]');
    const reviewLimit = Math.max(1, Math.min(5, Number(section.dataset.reviewLimit) || 5));

    try {
      await loadMaps(apiKey);
      const { Place } = await window.google.maps.importLibrary('places');
      const place = new Place({ id: placeId });

      await place.fetchFields({
        fields: ['displayName', 'rating', 'userRatingCount', 'reviews']
      });

      if (typeof place.rating === 'number' && ratingNode) {
        ratingNode.textContent = place.rating.toFixed(1);
      }

      if (typeof place.userRatingCount === 'number' && countNode) {
        countNode.textContent =
          place.userRatingCount.toLocaleString() +
          ' review' +
          (place.userRatingCount === 1 ? '' : 's');
      }

      if (summaryNode && typeof place.rating === 'number' && typeof place.userRatingCount === 'number') {
        summaryNode.setAttribute(
          'aria-label',
          'Google rating: ' +
            place.rating.toFixed(1) +
            ' out of 5 stars from ' +
            place.userRatingCount.toLocaleString() +
            ' reviews'
        );
      }

      const reviews = Array.isArray(place.reviews)
        ? place.reviews.filter((review) => review?.authorAttribution).slice(0, reviewLimit)
        : [];

      if (grid && reviews.length) {
        const fragment = document.createDocumentFragment();
        reviews.forEach((review) => fragment.appendChild(createReviewCard(review)));
        grid.replaceChildren(fragment);
        grid.setAttribute('aria-label', 'Live Google reviews');
      }

      if (badgeNode) badgeNode.hidden = false;
      section.dataset.liveLoaded = 'true';
    } catch (error) {
      console.warn('Glitz live Google reviews are using the static fallback.', error);
      section.dataset.liveLoaded = 'false';
    }
  };

  const hydratePurchaseTrust = async (node) => {
    const apiKey = (node.dataset.apiKey || '').trim();
    const placeId = (node.dataset.placeId || '').trim();
    if (!apiKey || !placeId) return;

    try {
      await loadMaps(apiKey);
      const { Place } = await window.google.maps.importLibrary('places');
      const place = new Place({ id: placeId });

      await place.fetchFields({
        fields: ['rating', 'userRatingCount']
      });

      if (typeof place.rating === 'number' && typeof place.userRatingCount === 'number') {
        const rating = node.querySelector('[data-purchase-rating]');
        const count = node.querySelector('[data-purchase-count]');
        const stars = node.querySelector('[data-purchase-stars]');
        const badge = node.querySelector('[data-purchase-live]');
        if (rating && count) {
          rating.textContent = place.rating.toFixed(1);
          count.textContent = place.userRatingCount.toLocaleString();
          if (stars) stars.textContent = makeStars(place.rating);
          if (badge) badge.hidden = false;
        } else {
          node.textContent = place.rating.toFixed(1) + ' Google Rating · ' + place.userRatingCount.toLocaleString() + ' Reviews';
        }
        node.setAttribute(
          'aria-label',
          'Google rating: ' +
            place.rating.toFixed(1) +
            ' out of 5 stars from ' +
            place.userRatingCount.toLocaleString() +
            ' reviews'
        );
        node.dataset.liveLoaded = 'true';
      }
    } catch (error) {
      console.warn('Glitz product trust Google rating is using the static fallback.', error);
      node.dataset.liveLoaded = 'false';
    }
  };

  const scheduleHydration = (node, hydrateNode) => {
    const start = () => {
      const enqueue = () => {
        const run = () => {
          if (node.isConnected) hydrateNode(node);
        };
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: 1500 });
        } else {
          window.setTimeout(run, 200);
        }
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          enqueue();
        }, { rootMargin: '400px 0px' });
        observer.observe(node.querySelector('[data-glitz-review-summary]') || node);
      } else {
        enqueue();
      }
    };
    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
  };

  const init = () => {
    document.querySelectorAll('[data-glitz-google-reviews]').forEach((section) => {
      if (section.dataset.glitzReviewsInitialized === 'true') return;
      section.dataset.glitzReviewsInitialized = 'true';
      scheduleHydration(section, hydrate);
    });

    document.querySelectorAll('[data-glitz-purchase-google]').forEach((node) => {
      if (node.dataset.glitzPurchaseGoogleInitialized === 'true') return;
      node.dataset.glitzPurchaseGoogleInitialized = 'true';
      scheduleHydration(node, hydratePurchaseTrust);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
